/**
 * StudiesDashboard — the tiled study view: every non-archived study at the
 * site as a tile with enrollment status and activity under the current lens,
 * ranked by activity; "enrolling but idle" flagged. Unmapped study-name
 * buckets render as explicit tiles instead of being dropped.
 */

import { useMemo } from 'react';
import { LayoutGrid, Loader2 } from 'lucide-react';
import { PageHeader, EmptyState } from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useInsights } from '../contexts';
import { useInsightsData, lensTotal, UNATTRIBUTED_KEY } from '../hooks/useInsightsData';
import { LensToggle } from '../components/LensToggle';
import { RangePicker } from '../components/RangePicker';
import { RefreshButton } from '../../ui';
import { DataQualityBanner } from '../components/DataQualityBanner';
import { StudyTile } from '../components/StudyTile';
import { ViewTabs } from '../components/ViewTabs';

export function StudiesDashboard() {
  const { currentSite } = useAuth();
  const { range, from, to, lens, setRange, setCustomRange, setLens } = useInsights();
  const data = useInsightsData(currentSite?.id || null, from, to);

  const studiesById = useMemo(() => new Map(data.studies.map((s) => [s.id, s])), [data.studies]);

  // Display names per coordinator key (for tile "top coordinators" lines).
  const coordinatorNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of data.coordinators) map.set(`user:${c.id}`, c.full_name || c.email || 'Unknown');
    for (const r of data.coordinatorRollups.values()) {
      if (!r.user_id) {
        map.set(r.key, r.key === UNATTRIBUTED_KEY ? 'Unattributed' : [...r.raw_names][0] || 'Unknown');
      }
      // r.user_id set but not present above (soft-deleted / access revoked
      // user) intentionally falls through unmapped — StudyTile's lookup
      // fallback renders "Former or removed user" for these.
    }
    return map;
  }, [data.coordinators, data.coordinatorRollups]);

  const tiles = useMemo(() => {
    const rows = [...data.studyRollups.values()].map((rollup) => ({
      rollup,
      study: rollup.protocol_id ? studiesById.get(rollup.protocol_id) || null : null,
      minutes: lensTotal(rollup.minutesByStatus, lens),
    }));
    rows.sort((a, b) => b.minutes - a.minutes || (a.study?.protocol_number || a.rollup.raw_name).localeCompare(b.study?.protocol_number || b.rollup.raw_name));
    let rank = 0;
    return rows.map((row) => ({ ...row, rank: row.minutes > 0 ? ++rank : null }));
  }, [data.studyRollups, studiesById, lens]);

  const maxMinutes = tiles.length > 0 ? Math.max(...tiles.map((t) => t.minutes)) : 0;

  // Data-quality aggregates for the banner.
  const quality = useMemo(() => {
    const unmappedCrc = new Set<string>();
    let unattributedMinutes = 0;
    for (const r of data.coordinatorRollups.values()) {
      if (r.key === UNATTRIBUTED_KEY) {
        unattributedMinutes = lensTotal(r.minutesByStatus, 'whole_visit');
      } else if (!r.user_id) {
        r.raw_names.forEach((n) => unmappedCrc.add(n));
      }
    }
    const unmappedStudies = [...data.studyRollups.values()]
      .filter((r) => !r.protocol_id && (r.visit_count > 0 || Object.keys(r.minutesByStatus).length > 0))
      .map((r) => r.raw_name);
    let inFlight = 0;
    let incomplete = 0;
    let overrideMinutes = 0;
    let overrideCount = 0;
    for (const v of data.visits) {
      inFlight += Number(v.in_flight_count || 0);
      incomplete += Number(v.incomplete_count || 0);
      overrideMinutes += Number(v.override_minutes || 0);
      overrideCount += Number(v.override_count || 0);
    }
    return { unattributedMinutes, unmappedCrc: [...unmappedCrc], unmappedStudies, inFlight, incomplete, overrideMinutes, overrideCount };
  }, [data.coordinatorRollups, data.studyRollups, data.visits]);

  const busy = data.loading || data.effortLoading;
  // An errored range must not render as confident zeros: with no effort/visits
  // every tile reads 0 minutes / 0 visits, which would light the "enrolling but
  // idle" flag on every enrolling study and let the data-quality banner speak
  // for data it never received. Unknown ≠ none.
  const activityKnown = !data.effortError;

  return (
    <div className="px-4 pt-4 pb-6 sm:px-6 lg:px-8 lg:pt-5">
      <div className="mx-auto w-full max-w-[1700px] space-y-4">
        <PageHeader
          title="Operations Insights"
          icon={LayoutGrid}
          subtitle="Study activity, coordinator time, and where effort could go next"
        />

        <ViewTabs />

        <div className="flex flex-wrap items-center gap-2">
          <RangePicker range={range} from={from} to={to} onRange={setRange} onCustom={setCustomRange} />
          {/* The shared page-header refresh primitive (ui/PageHeader) — evicts
              this site+range's cached data and re-fetches all 5 endpoints
              (Fix Spec C); data-only, never a page reload. */}
          <RefreshButton onClick={data.refresh} loading={busy} label="Refresh insights data" />
          <LensToggle lens={lens} onChange={setLens} />
          {busy && <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" />}
        </div>

        {data.error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {data.error}
          </div>
        )}

        {!busy && activityKnown && (
          <DataQualityBanner
            unattributedMinutes={quality.unattributedMinutes}
            unmappedCrcNames={quality.unmappedCrc}
            unmappedStudyNames={quality.unmappedStudies}
            inFlightCount={quality.inFlight}
            incompleteCount={quality.incomplete}
            overrideMinutes={quality.overrideMinutes}
            overrideCount={quality.overrideCount}
          />
        )}

        {!busy && tiles.length === 0 ? (
          <EmptyState
            icon={LayoutGrid}
            title="No studies yet"
            hint="Studies appear here once they exist in the registry (created in eReg) or visit data arrives on the Status Board."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {tiles.map((t) => (
              <StudyTile
                key={t.rollup.key}
                study={t.study}
                rollup={t.rollup}
                lens={lens}
                maxMinutes={maxMinutes}
                coordinatorNames={coordinatorNames}
                rank={t.rank}
                activityKnown={activityKnown}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-gray-400 dark:text-gray-500">
          Times come from Status Board status changes. Each visit records one coordinator, so co-staffed visits show
          under a single name.
        </p>
      </div>
    </div>
  );
}

export default StudiesDashboard;
